import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Users, Copy, Mail, Flame, Trophy, Activity, ChevronRight,
  UserMinus, ArrowLeft, BookOpen, TrendingUp, Crown,
} from "lucide-react";

export default function Roster() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [selected, setSelected] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [busy, setBusy] = useState(false);
  // student-side join
  const [joinCode, setJoinCode] = useState("");
  const [myMentors, setMyMentors] = useState([]);

  const isMentor = user?.role === "teacher" || user?.role === "parent";

  const refresh = async () => {
    if (isMentor) {
      try {
        const { data } = await api.get("/roster/mentor");
        setBoard(data);
      } catch { toast.error("Could not load roster"); }
    } else {
      try {
        const { data } = await api.get("/roster/my-mentors");
        setMyMentors(data.mentors || []);
      } catch { /* noop */ }
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [user?.role]);

  const copyCode = async () => {
    if (!board?.invite_code) return;
    try {
      await navigator.clipboard.writeText(board.invite_code);
      toast.success("Invite code copied");
    } catch { toast.error("Copy failed"); }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/roster/invite-email", { student_email: inviteEmail.trim() });
      if (data.status === "linked") toast.success(`${data.student_name || inviteEmail} added to your roster`);
      else if (data.status === "already_linked") toast.message("Already in your roster");
      else toast.success("Invite saved — they'll auto-link when they sign up");
      setInviteEmail("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };

  const joinByCode = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/roster/join", { code: joinCode.trim() });
      if (data.status === "already_linked") toast.message(`Already linked to ${data.mentor_name}`);
      else toast.success(`Joined ${data.mentor_name}'s roster`);
      setJoinCode("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Code not found"); }
    finally { setBusy(false); }
  };

  const unlink = async (sid, name) => {
    if (!window.confirm(`Remove ${name} from your roster?`)) return;
    try {
      await api.delete(`/roster/mentor/student/${sid}`);
      toast.success("Removed");
      setSelected(null);
      refresh();
    } catch { toast.error("Could not remove"); }
  };

  const openStudent = async (sid) => {
    try {
      const { data } = await api.get(`/roster/mentor/student/${sid}`);
      setSelected(data);
    } catch { toast.error("Could not load student"); }
  };

  // ============ STUDENT SIDE ============
  if (!isMentor) {
    return (
      <div className="space-y-6" data-testid="roster-student-page">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center"><Users size={20} /></div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>My Coaches</h1>
          </div>
          <p className="text-slate-600">Link a parent or teacher so they can support your writing journey.</p>
        </div>

        <div className="tactile-card p-6">
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Join with an invite code</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="join-code-input"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABC1234"
              className="flex-1 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-sky-400 px-4 py-3 outline-none uppercase tracking-widest font-mono"
            />
            <button
              data-testid="join-code-btn"
              onClick={joinByCode}
              disabled={busy || !joinCode.trim()}
              className="bg-sky-600 hover:bg-sky-500 text-white font-bold rounded-2xl px-6 py-3 disabled:opacity-60"
            >Join</button>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "Outfit" }}>Linked to</h2>
          {myMentors.length === 0 ? (
            <p className="text-slate-500 text-sm">No coaches linked yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {myMentors.map((m) => (
                <div key={m.mentor_id} data-testid={`my-mentor-${m.mentor_id}`} className="tactile-card p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-2xl grid place-items-center ${m.role === "teacher" ? "bg-indigo-100 text-indigo-700" : "bg-rose-100 text-rose-700"}`}>
                      <Users size={18} />
                    </div>
                    <div>
                      <div className="font-bold">{m.name}</div>
                      <div className="text-xs uppercase tracking-widest font-bold text-slate-500">{m.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ MENTOR SIDE ============
  if (selected) {
    const s = selected.student;
    return (
      <div className="space-y-6" data-testid="roster-detail-page">
        <button
          data-testid="back-to-roster"
          onClick={() => setSelected(null)}
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Back to roster
        </button>

        <div className="tactile-card p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{s.name}</h1>
              <p className="text-sm text-slate-500">
                {s.email} · <span className="capitalize">{s.grade_level}</span>{s.is_premium && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-bold uppercase">
                    <Crown size={10} /> Premium
                  </span>
                )}
              </p>
            </div>
            <button
              data-testid="unlink-student"
              onClick={() => unlink(s.student_id, s.name)}
              className="self-start inline-flex items-center gap-2 rounded-full border-2 border-rose-200 text-rose-600 hover:bg-rose-50 px-4 py-2 text-sm font-bold"
            >
              <UserMinus size={14} /> Remove
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Stat icon={Activity} color="indigo" label="Total sessions" v={s.total_sessions} />
            <Stat icon={Flame} color="orange" label="Current streak" v={s.streak} />
            <Stat icon={Trophy} color="amber" label="Badges" v={s.badges_earned} />
            <Stat icon={TrendingUp} color="emerald" label="Avg rubric" v={s.avg_rubric ?? "—"} />
          </div>

          {selected.rubric_trend?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-2">Recent rubric averages</div>
              <div className="flex items-end gap-2 h-32">
                {selected.rubric_trend.map((p, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full bg-emerald-400 rounded-t-lg" style={{ height: `${(p.avg / 4) * 100}%` }} />
                    <span className="text-[10px] text-slate-500">{p.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tactile-card p-6">
          <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Recent activity</h3>
          {selected.recent_sessions?.length === 0 ? (
            <p className="text-sm text-slate-500">No sessions yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {selected.recent_sessions.map((r) => (
                <li key={r.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold capitalize">{(r.kind || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-slate-400">{r.created_at?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roster-mentor-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-10 h-10 rounded-2xl grid place-items-center ${user?.role === "teacher" ? "bg-indigo-100 text-indigo-700" : "bg-rose-100 text-rose-700"}`}>
            <Users size={20} />
          </div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>
            {user?.role === "teacher" ? "Classroom Roster" : "Family Roster"}
          </h1>
        </div>
        <p className="text-slate-600">Invite your {user?.role === "teacher" ? "students" : "kids"} and see their writing progress at a glance.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="tactile-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={18} className="text-indigo-600" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Your invite code</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">Share this code — they'll enter it in their "My Coaches" page.</p>
          <div className="flex items-center gap-2">
            <div data-testid="invite-code" className="flex-1 rounded-2xl bg-slate-100 px-5 py-4 font-mono text-2xl font-bold tracking-[0.4em] text-center">
              {board?.invite_code || "—"}
            </div>
            <button
              data-testid="copy-invite-code"
              onClick={copyCode}
              className="rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white p-4"
              title="Copy code"
            >
              <Copy size={18} />
            </button>
          </div>
        </div>

        <div className="tactile-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={18} className="text-emerald-600" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Invite by email</h2>
          </div>
          <p className="text-sm text-slate-500 mb-3">If they already have an account, we'll link instantly. If not, we'll auto-link them at signup.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="invite-email-input"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="student@example.com"
              className="flex-1 rounded-2xl border-2 border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 px-4 py-3 outline-none"
            />
            <button
              data-testid="invite-email-btn"
              onClick={sendInvite}
              disabled={busy || !inviteEmail.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-2xl px-5 py-3 disabled:opacity-60"
            >Invite</button>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>
            Roster ({board?.student_count || 0})
          </h2>
        </div>
        {!board || board.students.length === 0 ? (
          <div className="tactile-card p-8 text-center text-sm text-slate-500" data-testid="roster-empty">
            No students yet. Share your invite code or send an email invite to get started.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {board.students.map((s) => (
              <button
                key={s.student_id}
                data-testid={`roster-student-${s.student_id}`}
                onClick={() => openStudent(s.student_id)}
                className="tactile-card p-5 text-left hover:border-indigo-200 transition group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-lg truncate" style={{ fontFamily: "Outfit" }}>{s.name}</div>
                    <div className="text-xs text-slate-500 truncate">{s.email}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 font-bold capitalize">{s.grade_level}</span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 px-2.5 py-1 font-bold">
                        <Flame size={11} /> {s.streak}d
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 font-bold">
                        <Trophy size={11} /> {s.badges_earned}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-700 px-2.5 py-1 font-bold">
                        <Activity size={11} /> {s.total_sessions}
                      </span>
                    </div>
                  </div>
                  <ChevronRight size={20} className="text-slate-300 group-hover:text-indigo-500 flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {board?.pending_invites?.length > 0 && (
        <div>
          <h3 className="text-sm uppercase font-bold tracking-widest text-slate-500 mb-2">Pending email invites</h3>
          <ul className="space-y-1">
            {board.pending_invites.map((p) => (
              <li key={p.id} data-testid={`pending-${p.id}`} className="text-sm text-slate-600">
                {p.student_email} <span className="text-xs text-slate-400">· will auto-link on signup</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, color, label, v }) => (
  <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100">
    <Icon size={16} className={`text-${color}-600 mb-1`} />
    <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{v}</div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
  </div>
);
